import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import UserCard from '../components/UserCard';
import PRInfoModal from '../components/PRInfoModal';
import Navigation from '../components/Navigation';
import ManualLink from '../components/ManualLink';
import { User } from '../types';
import './DashboardScreen.css';

export default function DashboardScreen() {
  const { allUsers, currentUser, loading } = useAuth();
  const [showPRModal, setShowPRModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  // 日本時間での今日の日付を取得
  const now = new Date();
  const jstOffset = 9 * 60; // JST is UTC+9
  const jstDate = new Date(now.getTime() + jstOffset * 60 * 1000);
  const todayJST = jstDate.toISOString().split('T')[0]; // YYYY-MM-DD format in JST

  // 今日スタンプを押したユーザーのみをフィルタリング（日付形式を統一して比較）
  const todayStampedUsers = allUsers
    .filter(user => {
      // organization01とorganization02が両方未設定の場合は、自分自身のみ表示
      if (!currentUser?.organization01 && !currentUser?.organization02) {
        return user.id === currentUser?.id;
      }

      // organization01またはorganization02のいずれかが一致するかチェック
      const matchesOrg01 = currentUser?.organization01 && (user.organization01 === currentUser.organization01 || user.organization02 === currentUser.organization01);
      const matchesOrg02 = currentUser?.organization02 && (user.organization01 === currentUser.organization02 || user.organization02 === currentUser.organization02);

      if (!matchesOrg01 && !matchesOrg02) {
        return false;
      }

      // user.stampsは 'YYYY-MM-DD' 形式、todayJSTも 'YYYY-MM-DD' 形式なので直接比較
      return user.stamps.includes(todayJST);
    })
    .sort((a, b) => b.name.localeCompare(a.name)); // 名前順でソート（リアルタイム更新のため）

  const handleShowPRModal = (user: User) => {
    setSelectedUser(user);
    setShowPRModal(true);
  };

  const handleClosePRModal = () => {
    setShowPRModal(false);
    setSelectedUser(null);
  };

  if (loading) {
    return (
      <div className="dashboard-container">
        <div className="dashboard-header">
          <h1 className="dashboard-title">タイムライン</h1>
        </div>
        <div style={{ padding: '40px', textAlign: 'center' }}>
          <p>読み込み中...</p>
        </div>
      </div>
    );
  }
  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <div className="header-logo">
          <img src="/images/100-day_logo-20251014.png" alt="100日実践" className="header-logo-image" />
        </div>
        <h1 className="dashboard-title">タイムライン</h1>
        <div className="header-spacer"></div>
      </div>

      <div className="manual-link-container-logged-in">
        <ManualLink />
      </div>

      <div className="dashboard-content">
        <p className="dashboard-subtitle">
          {todayStampedUsers.length > 0 
            ? `今日頑張った仲間たち` 
            : "今日は誰もスタンプを押していません"
          }
        </p>
        
        <div className="user-cards">
          {todayStampedUsers.length > 0 ? (
            todayStampedUsers.map((user) => (
              <UserCard 
                key={user.id} 
                user={user} 
                forceStampCompleted={true}
                onShowPRModal={handleShowPRModal}
              />
            ))
          ) : (
            <div style={{ 
              textAlign: 'center', 
              padding: '40px 20px', 
              color: '#666666' 
            }}>
              <p>今日スタンプを押したユーザーがいません。</p>
              <p>マイページでスタンプを押して、みんなを励ましましょう！</p>
            </div>
          )}
        </div>
        
        <div className="dashboard-footer">
          <p className="copyright">© 株式会社カフ．ウェブ．システム</p>
        </div>
      </div>

      <Navigation />
      
      {selectedUser && (
        <PRInfoModal
          isVisible={showPRModal}
          onClose={handleClosePRModal}
          user={selectedUser}
        />
      )}
    </div>
  );
}